import { FileTextIcon, FolderIcon } from "lucide-react";
import * as React from "react";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { FolderItem, Item } from "@/types/fse";

interface FilesSelectTreeRowProps {
  item: Item;
  onAction: (item: Item) => void;
  onFolderClick?: (folder: FolderItem) => void;
  actionButton: React.ReactNode | ((item: Item) => React.ReactNode);
  folderActionButton?: React.ReactNode | ((item: Item) => React.ReactNode);
  showFolders?: boolean;
}

export const FilesSelectTreeRow: React.FC<FilesSelectTreeRowProps> = ({
  item,
  onAction,
  onFolderClick,
  actionButton,
  folderActionButton,
  showFolders = true,
}) => {
  const { formatDate } = useDateFormatter();
  const isFolder = item.type === "folder";

  const handleClick = () => {
    if (isFolder && onFolderClick && showFolders) {
      onFolderClick(item);
    }
  };

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAction(item);
  };

  const renderActionButton = () => {
    if (typeof actionButton === "function") {
      return actionButton(item);
    }
    return actionButton;
  };

  const renderFolderActionButton = () => {
    if (!folderActionButton) return null;
    if (typeof folderActionButton === "function") {
      return folderActionButton(item);
    }
    return folderActionButton;
  };

  return (
    <div
      className="relative mb-2 flex flex-col items-start gap-2 rounded-lg border border-custom-blue-100 p-2 hover:bg-gray-50 md:flex-row md:items-center md:p-3"
      onClick={handleClick}
      style={{ cursor: isFolder && showFolders ? "pointer" : "default" }}
    >
      <div className="relative w-full md:w-1/2 lg:w-7/12">
        <div className="flex items-center">
          {isFolder ? <FolderIcon className="mr-2 h-4 w-4" /> : <FileTextIcon className="mr-2 h-4 w-4" />}
          <span className="truncate text-sm font-medium">{item.name}</span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center text-xs text-gray-500">
          <span className="mr-2">{formatDate(item.created_at)}</span>
        </div>
      </div>
      <div className="mt-1 flex w-full flex-wrap items-center justify-start gap-2 md:mt-0 md:w-1/2 md:justify-end lg:w-5/12">
        {isFolder && showFolders && folderActionButton && (
          <div onClick={handleAction}>{renderFolderActionButton()}</div>
        )}
        {(!isFolder || !showFolders) && <div onClick={handleAction}>{renderActionButton()}</div>}
      </div>
    </div>
  );
};
